import { Button, Header, InlineHeader, LottieIcon, ScrollView } from '$uikit';
import { useCurrentUserInfo, useDesktop, useTranslator } from '$hooks';
import './UploadVideo.sass';
import { useDispatch, useSelector } from 'react-redux';
import React, { useEffect, useRef, useState, ChangeEvent } from 'react';
import WebApp from '@twa-dev/sdk';
import { goBack } from '$navigation/helper';
import { attachActions, attachSelector } from '$store/attach';
import {
  _confirm,
  classNames,
  extractFramesFromVideo,
  formatFileSize,
  readFile,
} from '$utils';
import { beaconUploadEvent } from '$utils/uploadEvents';
import { RETRY_DELAYS } from '$utils/uploadRetry';
import { Loader, ProgressCircle, Slider } from '$shared/components';
import { IcCheck16, IcClose28, IcPlay28, IcUpload28 } from '$assets';
import { imActions } from '$store/im';
import { toastActions } from '$store/toast';
import { batchActions } from '$store';
import { useRoute } from 'react-router5';

const FRAMES_COUNT = 10;

enum LoaderContent {
  CLOSE = 'close',
  LOTTIE = 'lottie',
  PLAY = 'play',
}

export const UploadVideo = () => {
  const t = useTranslator();
  const dispatch = useDispatch();
  const isDesktop = useDesktop();
  const { route } = useRoute();
  const [videoSrc, setVideoSrc] = useState('');
  const [frames, setFrames] = useState<string[]>(Array(FRAMES_COUNT).fill(null));
  const [additionalCovers, setAdditionalCovers] = useState<string[]>([]);
  const [isFramesLoading, setIsFramesLoading] = useState(true);
  const [coverImageURL, setCoverImageURL] = useState('');
  const [isCovered, setIsCovered] = useState(true);
  const [loaderContent, setLoaderContent] = useState<LoaderContent>(LoaderContent.CLOSE);
  const videoElementRef = useRef<HTMLVideoElement>(null);
  const videoWrapperRef = useRef<HTMLDivElement>(null);
  const {
    video,
    isLoaded,
    loaded,
    total,
    pickerId,
    uploadData,
    isCancelled,
    asPeerId = 0,
    status,
    attempt,
    error,
    uploadId,
  } = useSelector(attachSelector).uploadingVideoData;
  const { id } = useCurrentUserInfo();
  const stateRef = useRef({ isLoaded, status, uploadId, loaded, total });
  stateRef.current = { isLoaded, status, uploadId, loaded, total };

  const isUploadActive = !isLoaded && status !== 'failed';
  // Retries during the save phase report waiting_network/retrying too.
  const isSaving =
    isLoaded &&
    (status === 'saving' || status === 'waiting_network' || status === 'retrying');

  useEffect(() => {
    import('../../assets/lottie/loader.json');

    return () => {
      if (!isCancelled) {
        // Cancel before clearing so the saga can still abort the S3 session.
        dispatch(attachActions.cancelUploadingVideo());
        dispatch(attachActions.clearUploadingVideoData());
        dispatch(attachActions.setIsAdditionalModalOpened(false));
        dispatch(imActions.setIsVideoUploading(false));
      }
    };
  }, []);

  // Keep the tab/app from being closed under an active upload.
  useEffect(() => {
    const inProgress = () => {
      const { isLoaded, status } = stateRef.current;
      return isLoaded
        ? status === 'saving' || status === 'waiting_network' || status === 'retrying'
        : status !== 'failed';
    };
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (inProgress()) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    const onPageHide = () => {
      const { status, uploadId, loaded, total } = stateRef.current;
      if (inProgress()) {
        beaconUploadEvent('video_upload_page_closed', {
          upload_id: uploadId,
          user_id: id,
          status,
          loaded,
          total,
        });
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    window.addEventListener('pagehide', onPageHide);
    try {
      WebApp.enableClosingConfirmation();
    } catch {}
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('pagehide', onPageHide);
      try {
        WebApp.disableClosingConfirmation();
      } catch {}
    };
  }, []);

  useEffect(() => {
    if (isCancelled) {
      goBack();
      batchActions(
        attachActions.clearUploadingVideoData(),
        attachActions.cancelUploadingVideo(),
        attachActions.setIsAdditionalModalOpened(false),
        imActions.setIsVideoUploading(false),
      );
    }
  }, [isCancelled]);

  useEffect(() => {
    if (video) {
      setVideoSrc(URL.createObjectURL(video));
    }
  }, [video]);

  // Frames are extracted only once the upload is done: reading the whole file
  // while it is being sent is what gets the tab killed on iOS.
  useEffect(() => {
    if (video && videoSrc && isLoaded && isFramesLoading) {
      extractFramesFromVideo(videoSrc, FRAMES_COUNT)
        .then((framesData) => {
          framesData.length && setFrames(Array.from(new Set(framesData)));
          setIsFramesLoading(false);
        })
        .catch((e) => {
          setIsFramesLoading(false);
          dispatch(toastActions.setToastFail(e.message));
        });
    }
  }, [video, videoSrc, isLoaded]);

  useEffect(() => {
    if (!isFramesLoading && !coverImageURL) {
      setCoverImageURL(frames[0]);
    }
  }, [isFramesLoading, coverImageURL]);

  useEffect(() => {
    if (!isLoaded) {
      setLoaderContent(LoaderContent.CLOSE);
    }

    if (isLoaded) {
      setTimeout(() => setLoaderContent(LoaderContent.LOTTIE), 175);
    }
  }, [isLoaded]);

  function handleChangeCover(imageURL: string) {
    setIsCovered(true);
    setCoverImageURL(imageURL);
    videoElementRef.current?.pause();
  }

  function handleCancelUploading() {
    _confirm(t('upload_video_cancel_confirmation'), () =>
      dispatch(attachActions.cancelUploadingVideo()),
    );
  }

  function handleBack() {
    if (isSaving) {
      // Saving keeps running in the background; the video attaches when it finishes.
      _confirm(
        t('upload_video_leave_saving_confirmation', {
          defaultValue: 'The video is still being saved. Leave anyway?',
        }),
        () => goBack(),
      );
      return;
    }
    if (isUploadActive) {
      _confirm(
        t('upload_video_leave_confirmation', {
          defaultValue: 'The upload is still in progress. Stop it?',
        }),
        () => dispatch(attachActions.cancelUploadingVideo()),
      );
      return;
    }
    dispatch(attachActions.cancelUploadingVideo());
  }

  function handleRetry() {
    dispatch(attachActions.retryUploadingVideo());
  }

  function handleRetrySave() {
    dispatch(attachActions.retrySavingVideo());
  }

  function handlePlayVideo() {
    setIsCovered(false);
    videoElementRef.current?.play()?.catch(() => {});
  }

  function handleUploadCover(event: ChangeEvent<HTMLInputElement>) {
    const { files } = event.target;
    if (files && files[0]) {
      readFile(files[0]).then((base64) => {
        handleChangeCover(base64);
        setAdditionalCovers([base64, ...additionalCovers]);
      });
    }
  }

  function handleSave() {
    if (!isLoaded || !uploadData || !videoElementRef.current || isSaving) {
      return;
    }

    dispatch(
      attachActions.saveUploadedVideo({
        profileId: id,
        pickerId,
        asPeerId,
        photo: {
          isVideo: true,
          photoMedium: coverImageURL,
          photoLarge: coverImageURL,
          width: 500,
          height: 400,
          photoId: -Date.now(),
        },
        uploadData: {
          ...uploadData,
          width: videoElementRef.current.videoWidth,
          height: videoElementRef.current.videoHeight,
          duration: Math.round(videoElementRef.current.duration),
          pickerId,
        },
      }),
    );
  }

  function renderLoader() {
    if (loaderContent === LoaderContent.LOTTIE) {
      return (
        <LottieIcon
          name="loader"
          size={72}
          onComplete={() => setLoaderContent(LoaderContent.PLAY)}
        />
      );
    }

    return (
      <>
        <ProgressCircle
          progress={total > 0 ? (loaded / total) * 100 : 0}
          radius={28}
          strokeWidth={2}
          isSpinning={status !== 'failed'}
          isDisableLoader={loaderContent === LoaderContent.PLAY}
        />
        <div className="UploadVideo__loader__icon">
          {loaderContent === LoaderContent.CLOSE ? <IcClose28 /> : <IcPlay28 />}
        </div>
      </>
    );
  }

  function renderCoverItem(isLoading: boolean, frame: string, key: string | number) {
    if (isLoading) {
      return (
        <div
          className={classNames('UploadVideo__cover__item', {
            _isLoading: true,
          })}
          key={key}
        >
          <Loader />
        </div>
      );
    }

    return (
      <div
        className={classNames('UploadVideo__cover__item', {
          _isSelected: frame === coverImageURL,
        })}
        key={key}
      >
        <>
          <img
            alt=""
            src={frame}
            className={classNames('UploadVideo__cover__item', {
              _isSelected: frame === coverImageURL,
            })}
            key={frame}
            onClick={() => handleChangeCover(frame)}
          />
          <div className="UploadVideo__cover__selected__icon">
            <IcCheck16 />
          </div>
        </>
      </div>
    );
  }

  const progressText = `${formatFileSize(loaded)} / ${formatFileSize(total)}`;
  const uploadingProgress = isLoaded
    ? t('upload_video_status_uploaded')
    : total > 0
    ? progressText
    : t('upload_video_status_preparing');

  function failedMessage(kind?: string, detail?: string) {
    switch (kind) {
      case 'file_unreadable':
        return t('upload_video_failed_file', {
          defaultValue:
            'The file is no longer available. Please go back and select the video again.',
        });
      case 'fatal':
      case 'not_found':
      case 'expired':
        return `${t('upload_video_failed_generic', {
          defaultValue: 'Could not upload the video.',
        })} ${detail || ''}`;
      case 'server':
        return t('upload_video_failed_server', {
          progress: progressText,
          defaultValue:
            'The server is not responding. %{progress} uploaded, you can continue.',
        });
      default:
        return t('upload_video_failed_network', {
          progress: progressText,
          defaultValue:
            'Could not upload the video: no connection. %{progress} uploaded, you can continue from here.',
        });
    }
  }

  function renderNetworkLabel() {
    if (status === 'waiting_network') {
      return (
        <div className="UploadVideo__loader__status__label _alert">
          {isLoaded
            ? t('upload_video_label_no_connection', { defaultValue: 'No connection' })
            : t('upload_video_label_no_connection_progress', {
                percent: total > 0 ? Math.floor((loaded / total) * 100) : 0,
                defaultValue: 'No connection · %{percent}% uploaded',
              })}
        </div>
      );
    }
    if (status === 'retrying') {
      return (
        <div className="UploadVideo__loader__status__label _alert">
          {t('upload_video_label_resuming', {
            attempt: attempt || 1,
            max: RETRY_DELAYS.length,
            defaultValue: '%{attempt}/%{max} Resuming upload…',
          })}
        </div>
      );
    }
    return null;
  }

  function renderStatus() {
    if (status === 'failed') {
      const fileError = error?.kind === 'file_unreadable';
      const message = failedMessage(error?.kind, error?.message);
      return (
        <div className="UploadVideo__status _failed">
          <div>{message}</div>
          <div className="UploadVideo__actions">
            {!fileError && (
              <Button onClick={handleRetry}>
                {t('upload_video_button_continue', { defaultValue: 'Continue' })}
              </Button>
            )}
            <Button mode="secondary" onClick={handleBack}>
              {t('upload_video_button_back', { defaultValue: 'Back' })}
            </Button>
          </div>
        </div>
      );
    }
    if (status === 'saving') {
      return (
        <div className="UploadVideo__status _waiting">
          {t('upload_video_saving', { defaultValue: 'Saving the video…' })}
        </div>
      );
    }
    if (status === 'save_failed') {
      return (
        <div className="UploadVideo__status _failed">
          <div>
            {t('upload_video_save_failed', {
              defaultValue: 'The video is uploaded but could not be saved.',
            })}
          </div>
          <div className="UploadVideo__actions">
            <Button onClick={handleRetrySave}>
              {t('upload_video_button_retry_save', { defaultValue: 'Try again' })}
            </Button>
            <Button mode="secondary" onClick={handleBack}>
              {t('upload_video_button_back', { defaultValue: 'Back' })}
            </Button>
          </div>
        </div>
      );
    }
    if (isLoaded) {
      return null;
    }
    return (
      <div className="UploadVideo__processing__label">
        {t('upload_video_processing_hint', {
          defaultValue:
            'The upload survives short connection drops. Please do not reload the page.',
        })}
      </div>
    );
  }

  if (!video) {
    return null;
  }

  return (
    <>
      <Header
        showBackButton
        backButtonOnClick={handleBack}
        className="UploadVideo__header"
      >
        {t('upload_video_title')}
      </Header>
      <ScrollView className="UploadVideo">
        <div className="UploadVideo__cont">
          <div
            className="UploadVideo__video"
            ref={videoWrapperRef}
            style={{
              height: videoElementRef.current?.videoHeight,
              maxHeight: isDesktop ? '350px' : videoWrapperRef.current?.clientWidth,
            }}
          >
            <div
              className={classNames('UploadVideo__video__cover', {
                _isEmpty: !coverImageURL,
              })}
            >
              {coverImageURL && (
                <img
                  alt=""
                  className="UploadVideo__loader__cover__image"
                  src={coverImageURL}
                ></img>
              )}
              <div className="UploadVideo__loader__labels">
                <div className="UploadVideo__loader__status__label">
                  {uploadingProgress}
                </div>
                {renderNetworkLabel()}
              </div>
              <div
                className={classNames('UploadVideo__loader', {
                  _isAnimated: loaderContent === LoaderContent.LOTTIE,
                })}
                onClick={isLoaded ? handlePlayVideo : handleCancelUploading}
              >
                {renderLoader()}
              </div>
            </div>
            <video
              controls
              ref={videoElementRef}
              style={{ zIndex: isCovered ? '-1' : '1' }}
            >
              <source type="video/mp4,video/quicktime" src={videoSrc} />
            </video>
          </div>
          {renderStatus()}
          <InlineHeader type="sub">{t('upload_video_select_cover')}</InlineHeader>
          <Slider className="UploadVideo__slider">
            <div className="UploadVideo__cover__item _uploadBtn">
              <IcUpload28 />
              {t('upload_video_upload_button')}
              <input onChange={handleUploadCover} accept="image/*" type="file" value="" />
            </div>
            {additionalCovers.map((cover) => renderCoverItem(false, cover, cover))}
            {frames.map((frame, index) =>
              renderCoverItem(isFramesLoading, frame, isFramesLoading ? index : frame),
            )}
          </Slider>
        </div>
      </ScrollView>
      <Button
        disabled={!isLoaded || !coverImageURL || isSaving || status === 'save_failed'}
        onClick={handleSave}
        className="UploadVideo__saveButton"
      >
        {t('upload_video_button_save')}
      </Button>
    </>
  );
};
